// The code a prompt holds (Poulpe-139, Poulpe-236): backtick code on one line,
// and fenced blocks with their language word. Nothing else of Markdown is
// read. Moved from today's Code tab (code/promptCode.ts there), without its
// colouring: the new pages have no highlighter yet, so a block is drawn as
// its text.

export interface PromptPart {
  kind: 'text' | 'inline' | 'block'
  /** Where it starts: its opening backtick or fence included. */
  start: number
  /** Its words: an inline code's or a block's code, without backticks or fence lines. */
  from: number
  to: number
  /** Where it ends: its closing backtick or fence line included, not the line break after it. */
  end: number
  /** A block's language word as typed, '' for none. */
  label: string
  /** A block has a closing fence. One that has none runs to the end of the text, as CommonMark has it. */
  closed: boolean
}

const part = (kind: PromptPart['kind'], start: number, from: number, to: number, end: number, label = '', closed = true): PromptPart => ({
  kind,
  start,
  from,
  to,
  end,
  label,
  closed,
})

export function promptParts(text: string): PromptPart[] {
  const parts: PromptPart[] = []
  const plain = (from: number, to: number) => {
    const inline = /(?<!`)`([^`\r\n]+)`(?!`)/g
    inline.lastIndex = from
    let at = from
    for (let match = inline.exec(text); match && match.index + match[0].length <= to; match = inline.exec(text)) {
      const end = match.index + match[0].length
      if (at < match.index) parts.push(part('text', at, at, match.index, match.index))
      parts.push(part('inline', match.index, match.index + 1, end - 1, end))
      at = end
    }
    if (at < to) parts.push(part('text', at, at, to, to))
  }
  const opening = /^```(?:[ \t]*([\w.+-]+))?[ \t]*\r?\n/gm
  const closing = /^```[ \t]*\r?$/gm
  let at = 0
  for (let match = opening.exec(text); match; match = opening.exec(text)) {
    plain(at, match.index)
    const label = match[1] ?? ''
    closing.lastIndex = opening.lastIndex
    const end = closing.exec(text)
    if (!end) {
      parts.push(part('block', match.index, opening.lastIndex, text.length, text.length, label, false))
      return parts
    }
    parts.push(part('block', match.index, opening.lastIndex, end.index, closing.lastIndex, label))
    at = closing.lastIndex
    opening.lastIndex = at
  }
  plain(at, text.length)
  return parts
}
